"use client";

import { ContentfulImageConfig } from "@2u-landing-pages/types";
import { getIconSelectionFromFields } from "@2u-landing-pages/utils";
import { getGradientOverlayStyle } from "@2u-landing-pages/utils/gradient-overlay-config";
import { getBorderRadius } from "@2u-landing-pages/utils/imageUtils";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { forwardRef } from "react";
import { CardProps, CustomCSSProperties } from "./Card.type";
import { LargeCard, MediumCard, SmallCard } from "./variations";

export const Card = forwardRef<HTMLDivElement, CardProps>((props, ref) => {
  const inspector = useContentfulInspectorMode();
  const style: CustomCSSProperties = {};
  const iconSelectionData = getIconSelectionFromFields(
    props?.entry?.fields as Record<string, unknown> | undefined,
    ["size", "color"]
  );

  const imageConfig = props?.entry?.fields?.image_config as unknown as ContentfulImageConfig;
  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;

  const desktopOverlayStyle = desktopCfg ? getGradientOverlayStyle(desktopCfg, "desktop") : {};
  const mobileOverlayStyle = mobileCfg ? getGradientOverlayStyle(mobileCfg, "mobile") : {};

  if (props?.entry?.fields?.background_color)
    style["--background-color"] = `var(${props.entry.fields.background_color})`;
  if (props?.entry?.fields?.initials_background_color)
    style["--initials-background-color"] = `var(${props.entry.fields.initials_background_color})`;
  if (props?.entry?.fields?.initials_text_color)
    style["--initials-color"] = `var(${props.entry.fields.initials_text_color})`;
  if (props?.entry?.fields?.heading_color) style["--heading-color"] = `var(${props.entry.fields.heading_color})`;
  if (props?.entry?.fields?.subhead_color) style["--subhead-color"] = `var(${props.entry.fields.subhead_color})`;
  if (props?.entry?.fields?.body_color) style["--body-color"] = `var(${props.entry.fields.body_color})`;
  const iconColor = iconSelectionData.color?.trim();
  if (iconColor) style["--icon-color"] = iconColor.startsWith("--") ? `var(${iconColor})` : iconColor;

  const isSvgImage = props?.entry?.fields?.image?.fields?.file?.contentType === "image/svg+xml";

  if (desktopCfg) {
    style["--aspect-ratio-desktop"] =
      desktopCfg.aspectRatio && desktopCfg.aspectRatio !== "original" ? desktopCfg.aspectRatio : undefined;
    style["--focal-point-desktop"] = desktopCfg?.focalPoint ?? "center";
    const desktopBorderRadius = getBorderRadius(desktopCfg?.r, isSvgImage);
    if (desktopBorderRadius !== undefined) {
      style["--border-radius-desktop"] = desktopBorderRadius;
    }
  }
  if (mobileCfg) {
    style["--aspect-ratio-mobile"] =
      mobileCfg.aspectRatio && mobileCfg.aspectRatio !== "original" ? mobileCfg.aspectRatio : undefined;
    style["--focal-point-mobile"] = mobileCfg?.focalPoint ?? "center";
    const mobileBorderRadius = getBorderRadius(mobileCfg?.r, isSvgImage);
    if (mobileBorderRadius !== undefined) {
      style["--border-radius-mobile"] = mobileBorderRadius;
    }
  }

  Object.assign(style, desktopOverlayStyle, mobileOverlayStyle);

  const variation = props.variationOverride || props.entry.fields.variation;

  switch (variation) {
    case "small":
      return <SmallCard ref={ref} {...props} style={style} iconSelectionData={iconSelectionData} inspector={inspector} />;
    case "medium":
      return <MediumCard ref={ref} {...props} style={style} iconSelectionData={iconSelectionData} inspector={inspector} />;
    case "large":
      return <LargeCard ref={ref} {...props} style={style} iconSelectionData={iconSelectionData} inspector={inspector} />;
    default:
      return null;
  }
});

Card.displayName = "Card";